import React, { useState, useEffect, useRef } from 'react';
import { settingsAPI } from '../../services/api';
import './SiteSettings.css';

const LogoUploader = ({ label, hint, currentUrl, variant, onUpdate }) => {
  const [preview, setPreview]   = useState(null);
  const [file, setFile]         = useState(null);
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setSaving(true);
    try {
      const response = await settingsAPI.uploadLogo(file, variant);
      if (response.success) {
        alert(`${label} uploaded successfully`);
        setFile(null);
        setPreview(null);
        if (inputRef.current) inputRef.current.value = '';
        onUpdate(response.logo_url);
      }
    } catch (err) {
      alert(err.response?.data?.message || `Failed to upload ${label}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove the current ${label}?`)) return;
    setDeleting(true);
    try {
      const response = await settingsAPI.deleteLogo(variant);
      if (response.success) {
        alert(`${label} removed`);
        onUpdate(null);
      }
    } catch {
      alert(`Failed to remove ${label}`);
    } finally {
      setDeleting(false);
    }
  };

  const cancel = () => {
    setFile(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const displayUrl = preview || currentUrl;

  return (
    <div className="logo-uploader-card">
      <div className="logo-uploader-header">
        <div>
          <h3>{label}</h3>
          <p className="logo-hint">{hint}</p>
        </div>
      </div>

      {/* Current / preview image */}
      <div className={`logo-preview-area ${!displayUrl ? 'empty' : ''}`}>
        {displayUrl ? (
          <img src={displayUrl} alt={label} className="logo-preview-img" />
        ) : (
          <div className="logo-no-image">
            <span>No logo uploaded</span>
          </div>
        )}
        {preview && (
          <span className="preview-badge">Preview — not saved yet</span>
        )}
      </div>

      {/* Actions */}
      <div className="logo-actions">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
          onChange={handleFileChange}
          className="file-input"
        />

        {file && (
          <div className="upload-row">
            <button onClick={handleUpload} disabled={saving} className="upload-btn">
              {saving ? 'Uploading...' : 'Save Logo'}
            </button>
            <button onClick={cancel} className="cancel-btn">Cancel</button>
          </div>
        )}

        {currentUrl && !file && (
          <button onClick={handleDelete} disabled={deleting} className="delete-logo-btn">
            {deleting ? 'Removing...' : '✕ Remove Logo'}
          </button>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const SiteSettings = () => {
  const [logoUrl, setLogoUrl]           = useState(null);
  const [logoWhiteUrl, setLogoWhiteUrl] = useState(null);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsAPI.get();
        if (response.success) {
          setLogoUrl(response.data.logo_url);
          setLogoWhiteUrl(response.data.logo_white_url);
        }
      } catch {
        // silently ignore; user will see "No logo uploaded"
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  if (loading) return <div className="loading">Loading settings...</div>;

  return (
    <div className="site-settings-container">
      <div className="settings-header">
        <h2>Site Settings</h2>
        <p className="settings-subtitle">Manage your website's branding and appearance.</p>
      </div>

      <div className="settings-section">
        <h4 className="section-title">Logo</h4>
        <p className="section-desc">
          Upload your company logo. Changes are reflected on the website immediately.
        </p>

        <div className="logo-uploaders-grid">
          <LogoUploader
            label="Main Logo"
            hint="Shown on white/light backgrounds (navbar, footer). PNG or SVG recommended."
            currentUrl={logoUrl}
            variant="logo"
            onUpdate={setLogoUrl}
          />
          <LogoUploader
            label="White Logo"
            hint="Shown on dark backgrounds. Use a white/light version of your logo."
            currentUrl={logoWhiteUrl}
            variant="logo-white"
            onUpdate={setLogoWhiteUrl}
          />
        </div>

        {/* Live preview strip */}
        <div className="preview-strip">
          <div className="preview-strip-light">
            <span className="preview-label">Navbar preview</span>
            {logoUrl
              ? <img src={logoUrl} alt="Logo preview" className="strip-logo" />
              : <span className="no-logo-text">No logo yet</span>
            }
          </div>
          <div className="preview-strip-dark">
            <span className="preview-label" style={{ color: '#aaa' }}>Dark background preview</span>
            {logoWhiteUrl
              ? <img src={logoWhiteUrl} alt="White logo preview" className="strip-logo" />
              : (logoUrl
                  ? <img src={logoUrl} alt="Logo preview" className="strip-logo" style={{ filter: 'brightness(0) invert(1)' }} />
                  : <span className="no-logo-text" style={{ color: '#666' }}>No logo yet</span>
                )
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteSettings;
